import React, { useState, useEffect, useCallback } from 'react';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    CloseOutlined
} from '@ant-design/icons';

const ToastNotification = ({
                               message,
                               type = 'info',
                               duration = 3000,
                               onClose,
                               id
                           }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);

    const handleClose = useCallback(() => {
        setIsLeaving(true);
        setTimeout(() => {
            onClose && onClose(id);
        }, 300);
    }, [onClose, id]);

    useEffect(() => {
        setTimeout(() => setIsVisible(true), 10);
    }, []);

    useEffect(() => {
        if (duration > 0) {
            const timer = setTimeout(() => {
                handleClose();
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [duration, handleClose]);

    const getIcon = (type) => {
        const iconStyle = { fontSize: '20px' };
        switch (type) {
            case 'success':
                return <CheckCircleOutlined style={{ ...iconStyle, color: 'var(--color-light-green)' }} />;
            case 'error':
                return <CloseCircleOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
            case 'warning':
                return <ExclamationCircleOutlined style={{ ...iconStyle, color: '#faad14' }} />;
            case 'info':
            default:
                return <InfoCircleOutlined style={{ ...iconStyle, color: '#1890ff' }} />;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'success': return 'var(--color-light-green)';
            case 'error': return '#ff4d4f';
            case 'warning': return '#faad14';
            case 'info':
            default: return '#1890ff';
        }
    };

    return (
        <div
            className={`modern-toast toast-${type} ${isVisible ? 'show' : ''} ${isLeaving ? 'hide' : ''}`}
            onClick={(e) => e.stopPropagation()}
            style={{
                background: 'var(--white-10)',
                border: `1px solid ${getTypeColor(type)}`,
                borderRadius: 'var(--radius-8)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                padding: '16px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                minWidth: '320px',
                maxWidth: '480px',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <div style={{ flexShrink: 0, marginTop: '2px' }}>
                {getIcon(type)}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    color: 'var(--color-white)',
                    fontSize: '16px',
                    fontWeight: '500',
                    marginBottom: '4px',
                    textTransform: 'capitalize'
                }}>
                    {type}
                </div>
                <div style={{
                    color: 'var(--white-80)',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    wordWrap: 'break-word'
                }}>
                    {message}
                </div>
            </div>

            <button
                onClick={handleClose}
                style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--white-60)',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: 'var(--radius-4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    flexShrink: 0
                }}
                onMouseOver={(e) => {
                    e.target.style.color = 'var(--color-white)';
                    e.target.style.background = 'var(--white-20)';
                }}
                onMouseOut={(e) => {
                    e.target.style.color = 'var(--white-60)';
                    e.target.style.background = 'none';
                }}
            >
                <CloseOutlined style={{ fontSize: '14px' }} />
            </button>

            {duration > 0 && (
                <div
                    className="toast-progress-bar"
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        height: '3px',
                        background: getTypeColor(type),
                        borderRadius: '0 0 var(--radius-8) var(--radius-8)',
                        animation: `progress ${duration}ms linear forwards`,
                        transformOrigin: 'left'
                    }}
                />
            )}
        </div>
    );
};

export default ToastNotification;